import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { simulateSwap, guardReassignAllowed, reconcileRoutesAfterReassignment } from '../services/routingEngineService';
import { parseDbTime } from '../utils/dbTime';
import { useEquipmentGate, EquipmentIssueList } from './EquipmentGate';   // __EQUIP_COMPAT__
import { cardFace } from '../utils/routeBuilderModel';
import { fetchFleet, fetchRules, quickHint } from '../services/equipmentService';

// __SWAP_PREVIEW__ (2026-09-12) Dedicated "what-if" panel: pick an assigned job and a
// target driver, dry-run BOTH drivers' full day (before/after) with every other stop's
// time shift, then Confirm -- which goes through the reassignment guard + route
// reconcile so the committed state is consistent. Nothing is written until Confirm.

// These render whatever the simulation hands back. Today that is engine-computed
// ISO with an offset, which parseDbTime passes through untouched -- but the panel
// also shows stop fields straight off load_stops, and those are naive UTC. Going
// through parseDbTime costs nothing and means one naive value reaching here later
// does not silently shift the whole preview.
const fmtT = (iso) => {
  const d = parseDbTime(iso);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--';
};
const minsBetween = (a, b) => {
  const x = parseDbTime(a), y = parseDbTime(b);
  if (!x || !y) return null;
  return Math.round((y - x) / 60000);
};
const shortAddr = (s) => (s || '').split(',')[0];

const labelStyle = { display: 'block', marginBottom: '0.35rem', color: 'var(--text-secondary)', fontSize: '0.85rem' };
const inputStyle = { width: '100%', padding: '0.6rem', backgroundColor: 'var(--surface-2)', border: '1px solid var(--border-default)', borderRadius: '5px', color: 'var(--text-primary)', fontSize: '0.95rem' };
const btn = { padding: '0.6rem 1.1rem', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' };

function Timeline({ title, result, compareTo, movedLoadId }) {
  if (!result) {
    return <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{title}: n/a</div>;
  }
  if (!result.success) {
    return (
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{title}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{result.message || 'No routable day'}</div>
      </div>
    );
  }
  const beforeById = {};
  ((compareTo && compareTo.success && compareTo.stopsWithETAs) || []).forEach((s) => { if (s && s.id) beforeById[s.id] = s; });
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
        {title} <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>({fmtT(result.estimatedStartTime)} to {fmtT(result.estimatedEndTime)}{result.totalDriveTime ? ', ' + result.totalDriveTime + ' driving' : ''})</span>
      </div>
      {(result.stopsWithETAs || []).map((s, i) => {
        const prev = s.id ? beforeById[s.id] : null;
        const delta = prev ? minsBetween(prev.estimatedArrival, s.estimatedArrival) : null;
        const isMoved = !!(movedLoadId && s.loadId === movedLoadId);
        const late = !!(s.isWithinTimeWindow && s.isWithinTimeWindow.status === 'late');
        const deltaColor = delta == null ? 'var(--text-muted)' : delta > 0 ? 'var(--color-danger)' : delta < 0 ? 'var(--color-success)' : 'var(--text-muted)';
        const deltaText = delta == null ? (compareTo ? 'new' : '') : (delta > 0 ? '+' + delta + 'm' : delta < 0 ? delta + 'm' : '0m');
        return (
          <div key={s.id || i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.8rem', padding: '0.2rem 0.25rem', borderBottom: '1px solid var(--border-default)', backgroundColor: isMoved ? 'rgba(230,126,34,0.15)' : 'transparent' }}>
            <span style={{ width: '5rem', color: s.stop_type === 'pickup' ? 'var(--color-warning)' : 'var(--color-success)', textTransform: 'uppercase', fontSize: '0.7rem' }}>{s.stop_type} #{s.stop_sequence}</span>
            <span style={{ flex: 1, color: 'var(--text-primary)' }}>{s.loadNumber ? s.loadNumber + ' ' : ''}{shortAddr(s.address)}{isMoved ? ' (moved here)' : ''}</span>
            <span title={s.dwellMinutes != null ? 'Stop time ' + s.dwellMinutes + ' min' + (s.dwellReason ? ' - ' + s.dwellReason : '') : undefined} style={{ width: '7.5rem', textAlign: 'right', color: 'var(--text-secondary)' }}>{fmtT(s.estimatedArrival)} - {fmtT(s.estimatedDeparture)}</span>
            <span style={{ width: '3.5rem', textAlign: 'right', color: deltaColor }}>{deltaText}</span>
            <span style={{ width: '2.5rem', color: 'var(--color-danger)', fontSize: '0.7rem' }}>{late ? 'LATE' : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

function DriverColumn({ label, driverName, before, after, movedLoadId }) {
  const endBefore = before && before.success ? before.estimatedEndTime : null;
  const endAfter = after && after.success ? after.estimatedEndTime : null;
  const endDelta = minsBetween(endBefore, endAfter);
  const lateAfter = ((after && after.success && after.stopsWithETAs) || []).filter((s) => s.isWithinTimeWindow && s.isWithinTimeWindow.status === 'late').length;
  return (
    <div style={{ flex: 1, minWidth: '300px', backgroundColor: 'var(--surface-2)', borderRadius: '6px', padding: '0.75rem' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '0.4rem', color: 'var(--accent-primary)' }}>{label}: {driverName}</div>
      {endDelta != null && (
        <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem', color: endDelta > 0 ? 'var(--color-danger)' : endDelta < 0 ? 'var(--color-success)' : 'var(--text-muted)' }}>
          Day ends {fmtT(endBefore)} to {fmtT(endAfter)} ({endDelta > 0 ? '+' : ''}{endDelta} min)
        </div>
      )}
      {lateAfter > 0 && (
        <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem', color: 'var(--color-danger)' }}>{lateAfter} stop{lateAfter === 1 ? '' : 's'} would miss its time window after the swap</div>
      )}
      <Timeline title="Before" result={before} />
      <Timeline title="After" result={after} compareTo={before} movedLoadId={movedLoadId} />
    </div>
  );
}

function SwapPreviewModal({ onClose }) {
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadId, setLoadId] = useState('');
  const [targetDriverId, setTargetDriverId] = useState('');
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  // __EQUIP_COMPAT__ per-driver equipment hint for the picked job + the hard warning on Confirm
  const [gateEquipment, equipmentModal] = useEquipmentGate();
  const [fleet, setFleet] = useState(null);
  const [rules, setRules] = useState(null);
  useEffect(() => { Promise.all([fetchFleet(), fetchRules()]).then(([f, r]) => { setFleet(f); setRules(r); }).catch(() => {}); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [loadsRes, driversRes] = await Promise.all([
          supabase.from('loads')
            // __OPEN_DECK__ billing_data: the Truck required / self-unload the equipment hints and the gate read (it was left out,
            // so an open-deck or tarp job was never checked here)
            .select('id, load_number, customer_name, driver_id, truck_id, scheduled_date, status, weight_kg, length_cm, width_cm, height_cm, commodity, billing_data, customer:customers(company_name), load_stops(*)')
            .not('driver_id', 'is', null)
            .not('status', 'in', '("complete","completed","cancelled","ready_to_bill","billed","archived")')
            .order('scheduled_date', { ascending: true }),
          supabase.from('drivers').select('id, name, user:user_id(name)').eq('is_available', true).order('name'),
        ]);
        if (loadsRes.error) throw loadsRes.error;
        if (driversRes.error) throw driversRes.error;
        setLoads(loadsRes.data || []);
        setDrivers(driversRes.data || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const driverName = (id) => {
    const d = drivers.find((x) => x.id === id);
    if (d) return d.user?.name || d.name || 'Driver';
    return id ? 'Driver ' + String(id).slice(0, 8) : '(none)';
  };
  const selectedLoad = loads.find((l) => l.id === loadId);
  // the moved job runs on the TARGET driver's unit, not on the truck it was booked with
  const hintFor = (driverId) => (selectedLoad ? quickHint({ driverId, loads: [{ ...selectedLoad, truck_id: null }], fleet, rules }) : null);
  const hintTag = (h) => (!h ? '' : h.level === 'block' ? '  - needs a reason / cannot' : h.level === 'warn' ? '  - equipment warnings' : '  - equipment fits');
  const targetHint = targetDriverId ? hintFor(targetDriverId) : null;

  const runPreview = async () => {
    if (!selectedLoad || !targetDriverId) { setError('Pick a job and a target driver.'); return; }
    if (targetDriverId === selectedLoad.driver_id) { setError('That job is already on that driver.'); return; }
    setBusy(true); setError(null); setSim(null); setStatus(null);
    try {
      const r = await simulateSwap({ loadId: selectedLoad.id, sourceDriverId: selectedLoad.driver_id, targetDriverId, date: selectedLoad.scheduled_date || null });
      if (r.error) setError('Simulation failed: ' + r.error);
      setSim(r);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirmSwap = async () => {
    if (!selectedLoad || !targetDriverId) return;
    setConfirming(true); setError(null);
    try {
      const g = await guardReassignAllowed(selectedLoad.id);
      if (g.tier === 'blocked') { setError(g.reason); return; }
      if (g.tier === 'override' && !window.confirm('WARNING: ' + g.reason + '\n\nOverride and reassign anyway?')) { return; }
      // __EQUIP_COMPAT__ the target driver's equipment vs the job - before anything is written
      const eq = await gateEquipment({ driverId: targetDriverId, loads: [{ ...selectedLoad, truck_id: null }], recordFor: [selectedLoad.id], context: 'swap_preview', title: 'Swap' });
      if (!eq.proceed) return;
      const { error: upErr } = await supabase.from('loads')
        .update({ driver_id: targetDriverId, status: 'assigned' }).eq('id', selectedLoad.id);
      if (upErr) throw upErr;
      await reconcileRoutesAfterReassignment({ loadId: selectedLoad.id, oldDriverId: selectedLoad.driver_id || null, newDriverId: targetDriverId, scheduledDate: selectedLoad.scheduled_date || null });
      try {   // __JOB_CHANGES__ both drivers hear it from the hub
        const { reportJobChanged } = await import('../services/hubPlanning');
        reportJobChanged(selectedLoad.id, selectedLoad, null, 'Swap Preview');
      } catch (e) { console.warn('job change not reported:', e); }
      setStatus('Swapped to ' + driverName(targetDriverId) + '. Both drivers\' routes were rebuilt.');
      setLoads((prev) => prev.map((l) => (l.id === selectedLoad.id ? { ...l, driver_id: targetDriverId, status: 'assigned' } : l)));
      setSim(null);
      setTargetDriverId('');
    } catch (e) { setError(e.message); } finally { setConfirming(false); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
      <div style={{ backgroundColor: 'var(--surface-1)', borderRadius: '10px', padding: '1.5rem', maxWidth: '1100px', width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', paddingBottom: '0.75rem', borderBottom: '1px solid var(--surface-2)' }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>Swap Preview</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '1.5rem', cursor: 'pointer' }}>x</button>
        </div>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          Pick a job and the driver to move it to. Preview shows both drivers' full day before and after, with every other stop's time shift, before anything is committed.
        </div>
        {error && <div style={{ backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', padding: '0.75rem', borderRadius: '5px', marginBottom: '0.75rem' }}>{error}</div>}
        {status && <div style={{ backgroundColor: 'var(--color-success)', color: 'var(--text-primary)', padding: '0.75rem', borderRadius: '5px', marginBottom: '0.75rem' }}>{status}</div>}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
          <div style={{ flex: 2, minWidth: '280px' }}>
            <label style={labelStyle}>Job (currently assigned)</label>
            <select value={loadId} onChange={(e) => { setLoadId(e.target.value); setSim(null); setStatus(null); }} style={inputStyle}>
              <option value="">Select a job...</option>
              {loads.map((l) => (
                <option key={l.id} value={l.id}>
                  {cardFace(l).customer + ' · ' + cardFace(l).route + ' - on ' + driverName(l.driver_id) + ' (' + l.status + (l.scheduled_date ? ', ' + l.scheduled_date : '') + ') · ' + (l.load_number || String(l.id).slice(0, 8))}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={labelStyle}>Move to driver</label>
            <select value={targetDriverId} onChange={(e) => { setTargetDriverId(e.target.value); setSim(null); setStatus(null); }} style={inputStyle}>
              <option value="">Select driver...</option>
              {drivers.filter((d) => !selectedLoad || d.id !== selectedLoad.driver_id).map((d) => (
                <option key={d.id} value={d.id}>{(d.user?.name || d.name || String(d.id).slice(0, 8)) + hintTag(hintFor(d.id))}</option>
              ))}
            </select>
          </div>
          <button onClick={runPreview} disabled={busy || !loadId || !targetDriverId} style={{ ...btn, backgroundColor: 'var(--accent-blue)', opacity: (busy || !loadId || !targetDriverId) ? 0.6 : 1 }}>{busy ? 'Simulating...' : 'Preview'}</button>
          <button onClick={confirmSwap} disabled={confirming || !sim || !!(sim && sim.error)} style={{ ...btn, backgroundColor: 'var(--accent-primary)', opacity: (confirming || !sim || !!(sim && sim.error)) ? 0.6 : 1 }}>{confirming ? 'Swapping...' : 'Confirm swap'}</button>
        </div>
        {targetHint && targetHint.result.issues.length > 0 && (
          <div style={{ marginBottom: '0.75rem', padding: '0.6rem', borderRadius: '5px', backgroundColor: 'var(--surface-2)', borderLeft: '4px solid ' + (targetHint.level === 'block' ? 'var(--color-danger)' : 'var(--color-warning)') }}>
            <EquipmentIssueList result={targetHint.result} compact />
          </div>
        )}
        {sim && !sim.error && selectedLoad && (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <DriverColumn label="From" driverName={driverName(selectedLoad.driver_id)} before={sim.before.source} after={sim.after.source} movedLoadId={null} />
            <DriverColumn label="To" driverName={driverName(targetDriverId)} before={sim.before.target} after={sim.after.target} movedLoadId={selectedLoad.id} />
          </div>
        )}
      </div>
      {equipmentModal}
    </div>
  );
}

export default SwapPreviewModal;
